import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  injectBxIcons,
  barChart05Icon,
  clockIcon,
  dataflow03Icon,
  flashIcon,
  signal01Icon,
  thermometer01Icon,
  wifiIcon,
} from '@beyonnex/bx-angular-ui';
import { formatDateTime } from '../../../models/core.models';
import { TEMPERATURE_PROBE } from '../../../models/temperature-probe.models';
import { DdmMeasurementUnit } from '../../../models/unit.models';
import { CommonTranslateService } from '../../../services/common-translate/common-translate.service';
import { isNullOrUndefined } from '../../../utils/utils';
import { CurrentStatePanelComponent } from '../current-state-panel/current-state-panel.component';
import { CurrentStateDataPoint } from '../current-state-panel/current-state-panel.models';
import { DeviceDetailsStore } from '../device-details.store';

@Component({
  selector: 'bxi-ts-current-state',
  imports: [CurrentStatePanelComponent],
  templateUrl: './ts-current-state.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TsCurrentStateComponent {
  private readonly store = inject(DeviceDetailsStore);
  protected readonly commonTranslate = inject(CommonTranslateService);

  protected readonly tsDevice = toSignal(this.store.tsDevice$);
  protected readonly gatewayLink = toSignal(this.store.gatewayLink$);
  protected readonly data = computed<CurrentStateDataPoint[]>(() => {
    const ts = this.tsDevice();
    const status = ts?.features?.temperatureMeasurement?.properties?.status;

    const temperatures: CurrentStateDataPoint[] = (
      [
        {
          probe: TEMPERATURE_PROBE.RED,
          label: 'features.temperatureMeasurement.properties.status.temperatureRed.value',
          reading: status?.temperatureRed,
          iconStyle: 'text-error-500',
        },
        {
          probe: TEMPERATURE_PROBE.WHITE,
          label: 'features.temperatureMeasurement.properties.status.temperatureWhite.value',
          reading: status?.temperatureWhite,
          iconStyle: 'text-utility-gray-400',
        },
        {
          probe: TEMPERATURE_PROBE.BLACK,
          label: 'features.temperatureMeasurement.properties.status.temperatureBlack.value',
          reading: status?.temperatureBlack,
          iconStyle: 'text-utility-gray-900',
        },
      ] as const
    )
      .filter(({ reading }) => !isNullOrUndefined(reading?.value))
      .map(({ probe, label, reading, iconStyle }) => ({
        label: this.commonTranslate.translateThingProperty(label),
        subLabel: this.commonTranslate.translateProbeMapping(ts?.getProbeMappingLabels(probe)),
        value: this.commonTranslate.formatUnit(reading?.unit as DdmMeasurementUnit, reading?.value),
        icon: 'thermometer-01',
        iconStyle,
      }));

    return [
      ...temperatures,
      {
        label: this.commonTranslate.translateThingProperty('features.battery.properties.status.voltage'),
        value: `${this.commonTranslate.formatUnit(ts?.features?.battery?.properties?.status?.voltage?.unit as DdmMeasurementUnit, ts?.features?.battery?.properties?.status?.voltage?.value)}`,
        icon: 'flash',
        iconStyle: 'text-fg-warning-primary',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.grid.properties.rssi'),
        value: ts?.features?.grid?.properties?.rssi,
        icon: 'bar-chart-05',
        iconStyle: 'text-teal-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.grid.properties.snr'),
        value: ts?.features?.grid?.properties?.snr,
        icon: 'wifi',
        iconStyle: 'text-utility-purple-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.grid.properties.dataRate'),
        value: ts?.features?.grid?.properties?.dataRate,
        icon: 'dataflow-03',
        iconStyle: 'text-utility-blue-light-600',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.grid.properties.gateway'),
        value: ts?.features?.grid?.properties?.gateway,
        icon: 'signal-01',
        iconStyle: 'text-utility-gray-blue-500',
        ...this.gatewayLink(),
      },
      {
        label: this.commonTranslate.translateThingProperty('features.grid.properties.receiveTime'),
        value: formatDateTime(ts?.features?.grid?.properties?.receiveTime),
        icon: 'clock',
        iconStyle: 'text-utility-blue-light-600',
      },
    ];
  });

  constructor() {
    injectBxIcons([thermometer01Icon, flashIcon, barChart05Icon, wifiIcon, dataflow03Icon, signal01Icon, clockIcon]);
  }
}
